import type { ReactNode } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2ExplainerBanner.module.scss";
import type { TenantIconNameEnum } from "@/types/tenantIcons";

const ACCORDION_ITEM = "grid-v2-explainer";

export interface GridV2ExplainerBannerProps {
  /** Tenant glyph drawn in the turquoise circle; decorative. */
  icon: TenantIconNameEnum;
  /** Names the trigger button; stays visible when collapsed. */
  title: string;
  /** The explanation, inline content: the shell renders it as a paragraph. */
  children: ReactNode;
  className?: string;
}

/**
 * Collapsible card above the Grid V2 flight results that tells the shopper why
 * their points balance limits the pricing they see. Open on arrival;
 * collapsing keeps the icon and the title.
 */
export const GridV2ExplainerBanner = ({
  icon,
  title,
  children,
  className,
}: GridV2ExplainerBannerProps) => {
  const [block, cn] = useModuleBEM(styles, "GridV2ExplainerBanner");

  return (
    <div className={clsx(block, className)}>
      <Accordion
        type="single"
        collapsible
        defaultValue={ACCORDION_ITEM}
        className={cn("accordion")}
      >
        <AccordionItem value={ACCORDION_ITEM} className={cn("item")}>
          <AccordionTrigger className={cn("trigger")}>
            <span className={cn("icon")} aria-hidden="true">
              <Icon iconName={icon} />
            </span>
            <span className={cn("title")}>{title}</span>
          </AccordionTrigger>
          <AccordionContent className={cn("content")}>
            <p className={cn("body")}>{children}</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
